import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { toast } from "@/components/toast/CustomToast";
import type { RosterConfigObject } from "@/hooks/useRosterConfig";
import { saveRosterConfigApi } from "@/pages/roster-create/services/rosterCreateApi";

type UseRosterUseMidControlParams = {
  readonly serverConfig: RosterConfigObject | undefined;
  readonly isAdmin: boolean;
  readonly selectedGroupId: string | null | undefined;
  readonly resolvedManpowerOid: string | null | undefined;
  readonly resolvedManpowerGid: string | null | undefined;
  readonly manpowerYear: number | null | undefined;
  readonly manpowerMonth: number | null | undefined;
};

type UseMidMutationContext = {
  readonly previousUseMid: boolean | undefined;
};

const USE_MID_SAVE_DEBOUNCE_MS = 400;

export function normalizeUseMidValue(value: unknown): boolean | undefined {
  if (value == null) return undefined;
  if (value === false || value === 0) return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "false" || normalized === "0") return false;
  }
  return true;
}

export function useRosterUseMidControl({
  serverConfig,
  isAdmin,
  selectedGroupId,
  resolvedManpowerOid,
  resolvedManpowerGid,
  manpowerYear,
  manpowerMonth,
}: UseRosterUseMidControlParams) {
  const queryClient = useQueryClient();
  const [localUseMid, setLocalUseMid] = useState<boolean | undefined>();
  const [isDebouncePending, setIsDebouncePending] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const serverUseMid = normalizeUseMidValue(serverConfig?.use_mid);
  const committedUseMidRef = useRef<boolean | undefined>(serverUseMid);
  const isUseMid = localUseMid ?? serverUseMid;

  const setOptimisticUseMid = useCallback(
    (nextUseMid: boolean) => {
      const configQueryGroupId = isAdmin ? (selectedGroupId ?? "") : "";
      setLocalUseMid(nextUseMid);
      queryClient.setQueryData(
        QUERY_KEYS.config.configData(configQueryGroupId),
        serverConfig ? { ...serverConfig, use_mid: nextUseMid } : serverConfig
      );
    },
    [isAdmin, queryClient, selectedGroupId, serverConfig]
  );

  const saveUseMidMutation = useMutation<
    unknown,
    Error,
    boolean,
    UseMidMutationContext
  >({
    mutationFn: async (nextUseMid) => {
      if (!serverConfig) {
        throw new Error("현재 설정 정보를 불러오지 못했습니다.");
      }

      await saveRosterConfigApi({
        config: {
          ...serverConfig,
          use_mid: nextUseMid,
        },
        groupId: isAdmin && selectedGroupId ? selectedGroupId : undefined,
      });
    },
    onMutate: (nextUseMid) => {
      const previousUseMid = committedUseMidRef.current ?? serverUseMid;
      setOptimisticUseMid(nextUseMid);

      return { previousUseMid };
    },
    onSuccess: async (_data, nextUseMid) => {
      committedUseMidRef.current = nextUseMid;
      await Promise.all([
        resolvedManpowerGid
          ? queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.shifts.shiftManager(resolvedManpowerGid),
            })
          : Promise.resolve(),
        resolvedManpowerOid &&
        resolvedManpowerGid &&
        manpowerYear != null &&
        manpowerMonth != null
          ? queryClient.invalidateQueries({
              queryKey: QUERY_KEYS.manpower.monthly(
                resolvedManpowerOid,
                resolvedManpowerGid,
                manpowerYear,
                manpowerMonth
              ),
            })
          : Promise.resolve(),
      ]);
    },
    onError: (error, _nextUseMid, context) => {
      const fallbackUseMid = context?.previousUseMid ?? serverUseMid;
      committedUseMidRef.current = fallbackUseMid;
      setLocalUseMid(fallbackUseMid);
      const configQueryGroupId = isAdmin ? (selectedGroupId ?? "") : "";
      queryClient.setQueryData(
        QUERY_KEYS.config.configData(configQueryGroupId),
        serverConfig ? { ...serverConfig, use_mid: fallbackUseMid } : serverConfig
      );
      toast.error(error.message || "M 근무 사용 설정 저장에 실패했습니다.");
    },
  });

  useEffect(() => {
    if (saveUseMidMutation.isPending || isDebouncePending) return;
    committedUseMidRef.current = serverUseMid;
    setLocalUseMid(serverUseMid);
  }, [isDebouncePending, saveUseMidMutation.isPending, serverUseMid]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const debouncedSetUseMid = useCallback(
    (nextUseMid: boolean) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setOptimisticUseMid(nextUseMid);
      setIsDebouncePending(true);

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        setIsDebouncePending(false);
        saveUseMidMutation.mutate(nextUseMid);
      }, USE_MID_SAVE_DEBOUNCE_MS);
    },
    [saveUseMidMutation, setOptimisticUseMid]
  );

  return {
    isUseMid,
    isUseMidSaving: saveUseMidMutation.isPending,
    debouncedSetUseMid,
    saveUseMidMutation,
  };
}
